/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — aje-handler.ts
   Adjusting Journal Entries: add, remove, edit, render, post, unpost,
   event delegation (click + change)
   ═══════════════════════════════════════════════════════════════════════ */
// AJE = Adjusting Journal Entries — end-of-period accounting adjustments

import {
  state,
  SECTIONS,
  SECTION_SIGN,
  type SectionData,
  type AJEEntry,
  type AJELine,
} from './state';
import { esc, fmt, el, elInput } from './utils';
import { saveProject } from './data';
import { buildTrialBalanceEditor } from './tb-editor';

// ─── Local Types ───────────────────────────────────────────────────────────

interface SectionGroupOption {
  value: string;
  text: string;
}

interface SectionGroup {
  label: string;
  options: SectionGroupOption[];
}

// ─── Local AJE Section Groups (richer structure for <optgroup> rendering) ──

/**
 * AJE section groups with labels for <optgroup> rendering.
 * Different structure from SECTION_GROUPS in state.ts (which is string[][]).
 */
const AJE_SECTION_GROUPS: SectionGroup[] = [
  { label: 'Income Statement', options: [
    { value: 'revenue',    text: 'Revenue' },
    { value: 'cogs',       text: 'Cost of Goods Sold' },
    { value: 'opex',       text: 'Operating Expenses' },
    { value: 'other',      text: 'Other Income / Expense' },
  ]},
  { label: 'Balance Sheet', options: [
    { value: 'current-assets',    text: 'Current Assets' },
    { value: 'noncurrent-assets', text: 'Non-Current Assets' },
    { value: 'current-liab',      text: 'Current Liabilities' },
    { value: 'noncurrent-liab',   text: 'Non-Current Liabilities' },
    { value: 'equity',            text: "Shareholders' Equity" },
  ]},
  { label: 'Cash Flow', options: [
    { value: 'cf-operating',  text: 'Operating Activities' },
    { value: 'cf-investing',  text: 'Investing Activities' },
    { value: 'cf-financing',  text: 'Financing Activities' },
  ]},
];

// ─── Helpers ───────────────────────────────────────────────────────────────

function buildSectionOptionsHTML(): string {
  let html = '<option value="">Select section...</option>';
  AJE_SECTION_GROUPS.forEach(function(g: SectionGroup) {
    html += '<optgroup label="' + esc(g.label) + '">';
    g.options.forEach(function(o: SectionGroupOption) { html += '<option value="' + o.value + '">' + esc(o.text) + '</option>'; });
    html += '</optgroup>';
  });
  return html;
}

// ─── AJE CRUD ──────────────────────────────────────────────────────────────

export function addAJEEntry(): void {
  state.ajeEntries.push({ description: '', date: '', lines: [{ account: '', section: '', debit: 0, credit: 0 }] } as AJEEntry);
  renderAJEEntries(); saveProject();
}

export function removeAJEEntry(entryIdx: number): void {
  if (!confirm('Remove AJE-' + (entryIdx + 1) + '?')) return;
  state.ajeEntries.splice(entryIdx, 1);
  renderAJEEntries(); saveProject();
}

export function addAJELine(entryIdx: number): void {
  state.ajeEntries[entryIdx].lines.push({ account: '', section: '', debit: 0, credit: 0 });
  renderAJEEntries(); saveProject();
}

export function removeAJELine(entryIdx: number, lineIdx: number): void {
  if (state.ajeEntries[entryIdx].lines.length <= 1) return;
  state.ajeEntries[entryIdx].lines.splice(lineIdx, 1);
  renderAJEEntries(); saveProject();
}

export function ajeFieldChange(entryIdx: number, field: string, value: string): void {
  (state.ajeEntries[entryIdx] as any)[field] = value; saveProject();
}

export function ajeLineChange(entryIdx: number, lineIdx: number, field: string, value: string): void {
  if (field === 'debit' || field === 'credit') (state.ajeEntries[entryIdx].lines[lineIdx] as any)[field] = parseFloat(value) || 0;
  else (state.ajeEntries[entryIdx].lines[lineIdx] as any)[field] = value;
  renderAJEEntries(); saveProject();
}

// ─── Render ────────────────────────────────────────────────────────────────

export function renderAJEEntries(): void {
  const container = el('aje-entries-container');
  if (!container) return;
  const secOpts = buildSectionOptionsHTML();
  let html = '', totalDebits = 0, totalCredits = 0;
  state.ajeEntries.forEach(function(entry: AJEEntry, eIdx: number) {
    let eD = 0, eC = 0;
    entry.lines.forEach(function(ln: AJELine) { eD += parseFloat(String(ln.debit)) || 0; eC += parseFloat(String(ln.credit)) || 0; });
    totalDebits += eD; totalCredits += eC;
    const balanced = Math.abs(eD - eC) < 0.005;
    const oob = (!balanced && (eD > 0 || eC > 0)) ? ' out-of-balance' : '';
    html += '<div class="aje-entry' + oob + '"><div class="aje-header">';
    html += '<span class="aje-num">AJE-' + (eIdx + 1) + '</span>';
    html += '<input type="date" value="' + (entry.date || '') + '" data-aje-change="field" data-entry="' + eIdx + '" data-field="date">';
    html += '<input type="text" value="' + esc(entry.description || '') + '" data-aje-change="field" data-entry="' + eIdx + '" data-field="description" placeholder="Memo / Description" style="flex:1;min-width:150px">';
    if (state.ajePosted) html += '<span class="aje-posted-badge posted">Posted</span>';
    html += '<button class="btn btn-danger btn-sm" data-aje-action="removeEntry" data-entry="' + eIdx + '" style="margin-left:auto">Remove</button></div>';
    html += '<table class="aje-lines-table"><thead><tr><th>Account</th><th>Section</th><th style="width:80px"></th><th>Debit</th><th>Credit</th></tr></thead><tbody>';
    entry.lines.forEach(function(ln: AJELine, lIdx: number) {
      html += '<tr><td><input type="text" value="' + esc(ln.account || '') + '" data-aje-change="line" data-entry="' + eIdx + '" data-line="' + lIdx + '" data-field="account" placeholder="Account name"></td>';
      html += '<td><select data-aje-change="line" data-entry="' + eIdx + '" data-line="' + lIdx + '" data-field="section">' + secOpts.replace('value="' + ln.section + '"', 'value="' + ln.section + '" selected') + '</select></td>';
      html += '<td style="text-align:center">';
      if (entry.lines.length > 1) html += '<button class="btn btn-ghost btn-sm" data-aje-action="removeLine" data-entry="' + eIdx + '" data-line="' + lIdx + '" style="padding:2px 6px;font-size:0.7rem">&times;</button>';
      html += '</td><td><input type="number" value="' + (ln.debit || '') + '" data-aje-change="line" data-entry="' + eIdx + '" data-line="' + lIdx + '" data-field="debit" step="0.01" placeholder="0.00"></td>';
      html += '<td><input type="number" value="' + (ln.credit || '') + '" data-aje-change="line" data-entry="' + eIdx + '" data-line="' + lIdx + '" data-field="credit" step="0.01" placeholder="0.00"></td></tr>';
    });
    html += '</tbody></table><div class="aje-footer"><button class="btn btn-ghost btn-sm" data-aje-action="addLine" data-entry="' + eIdx + '">Add Line</button>';
    html += '<div class="aje-totals" style="margin-left:auto"><span>Debits: <strong>' + fmt(eD) + '</strong></span><span>Credits: <strong>' + fmt(eC) + '</strong></span>';
    if (eD > 0 || eC > 0) html += balanced ? '<span class="balanced">Balanced</span>' : '<span class="unbalanced">Out of Balance (' + fmt(Math.abs(eD - eC)) + ')</span>';
    html += '</div></div></div>';
  });
  container.innerHTML = html;
  const summaryEl = el('aje-summary');
  if (summaryEl) {
    if (state.ajeEntries.length > 0) {
      summaryEl.style.display = 'block';
      const cSym = elInput('currency')?.value ?? '$';
      summaryEl.innerHTML = '<strong>' + state.ajeEntries.length + '</strong> entries, <strong>' + cSym + fmt(totalDebits) + '</strong> total debits' + (state.ajePosted ? ' &mdash; <span class="aje-posted-badge posted">Posted</span>' : '');
    } else { summaryEl.style.display = 'none'; }
  }
  const unpostBtn = el('aje-unpost-btn');
  if (unpostBtn) unpostBtn.style.display = state.ajePosted ? 'inline-block' : 'none';
}

// ─── Post / Unpost ─────────────────────────────────────────────────────────

export function postAllAJE(): void {
  if (state.ajeEntries.length === 0) { alert('No journal entries to post.'); return; }
  const errors: string[] = [];
  state.ajeEntries.forEach(function(entry: AJEEntry, eIdx: number) {
    let d = 0, c = 0;
    entry.lines.forEach(function(ln: AJELine) { d += parseFloat(String(ln.debit)) || 0; c += parseFloat(String(ln.credit)) || 0; });
    if (Math.abs(d - c) >= 0.005) errors.push('AJE-' + (eIdx + 1) + ' is out of balance');
    entry.lines.forEach(function(ln: AJELine, i: number) { if (!ln.section) errors.push('AJE-' + (eIdx + 1) + ', line ' + (i + 1) + ': missing section'); });
  });
  if (errors.length > 0) { alert('Cannot post:\n\n' + errors.join('\n')); return; }
  state.ajePrePostData = {} as SectionData;
  SECTIONS.forEach(function(s: string) { state.ajePrePostData![s] = JSON.parse(JSON.stringify(state.currentData[s])); });
  let posted = 0;
  state.ajeEntries.forEach(function(entry: AJEEntry, eIdx: number) {
    entry.lines.forEach(function(ln: AJELine) {
      const sec = ln.section;
      const rawNet = (parseFloat(String(ln.debit)) || 0) - (parseFloat(String(ln.credit)) || 0);
      if (rawNet === 0) return;
      const net = (SECTION_SIGN[sec] || 1) * rawNet;
      let found = false;
      for (let i = 0; i < state.currentData[sec].length; i++) {
        if (state.currentData[sec][i].label === ln.account) {
          state.currentData[sec][i].amount = (parseFloat(String(state.currentData[sec][i].amount)) || 0) + net;
          found = true;
          break;
        }
      }
      if (!found) state.currentData[sec].push({ label: ln.account || ('AJE-' + (eIdx + 1) + ' Adjustment'), amount: net });
    });
    posted++;
  });
  state.ajePosted = true; saveProject(); renderAJEEntries(); buildTrialBalanceEditor();
  const st = el('aje-status');
  if (st) {
    st.innerHTML = '<div class="import-status success">Successfully posted ' + posted + ' adjusting entries.</div>';
    setTimeout(function() { st.innerHTML = ''; }, 4000);
  }
}

export function unpostAJE(): void {
  if (!state.ajePosted) return;
  if (!confirm('Reverse all posted adjustments?')) return;
  if (state.ajePrePostData) SECTIONS.forEach(function(s: string) { if (state.ajePrePostData![s]) state.currentData[s] = JSON.parse(JSON.stringify(state.ajePrePostData![s])); });
  state.ajePosted = false; state.ajePrePostData = null;
  saveProject(); renderAJEEntries(); buildTrialBalanceEditor();
  const st = el('aje-status');
  if (st) {
    st.innerHTML = '<div class="import-status info">Adjustments reversed.</div>';
    setTimeout(function() { st.innerHTML = ''; }, 4000);
  }
}

// ---------------------------------------------------------------------------
// Event Delegation — AJE click actions
// ---------------------------------------------------------------------------

document.addEventListener('click', function (e: MouseEvent) {
  const target = (e.target as HTMLElement).closest('[data-aje-action]') as HTMLElement | null;
  if (!target) return;

  const action = target.dataset.ajeAction;
  const entryIdx = target.dataset.entry !== undefined ? parseInt(target.dataset.entry, 10) : -1;

  switch (action) {
    case 'removeEntry':
      if (entryIdx >= 0) removeAJEEntry(entryIdx);
      break;
    case 'removeLine': {
      const lineIdx = target.dataset.line !== undefined ? parseInt(target.dataset.line, 10) : -1;
      if (entryIdx >= 0 && lineIdx >= 0) removeAJELine(entryIdx, lineIdx);
      break;
    }
    case 'addLine':
      if (entryIdx >= 0) addAJELine(entryIdx);
      break;
  }
});

// ---------------------------------------------------------------------------
// Event Delegation — AJE change handlers
// ---------------------------------------------------------------------------

document.addEventListener('change', function (e: Event) {
  const target = e.target as HTMLElement;
  if (!target.dataset || !target.hasAttribute('data-aje-change')) return;

  const changeType = target.getAttribute('data-aje-change');
  const entryIdx = parseInt(target.getAttribute('data-entry') || '-1', 10);
  const field = target.getAttribute('data-field') || '';
  const value = (target as HTMLInputElement | HTMLSelectElement).value;

  if (changeType === 'field' && entryIdx >= 0 && field) {
    ajeFieldChange(entryIdx, field, value);
  } else if (changeType === 'line' && entryIdx >= 0 && field) {
    const lineIdx = parseInt(target.getAttribute('data-line') || '-1', 10);
    if (lineIdx >= 0) ajeLineChange(entryIdx, lineIdx, field, value);
  }
});
